import { customAmplitude } from '@/utils/customAmplitude'

/** Always attached to Batch `Selected tool` so charts can split editor vs batch. */
export const BATCH_PLACEMENT = 'Batch' as const

/**
 * Tool open strings for `Selected tool` + `Tool`.
 * Prefer editor-parity names where the same tool exists in the editor.
 */
export const BATCH_TOOLS = {
  background: 'bazaart.background',
  resize: 'bazaart.resize',
  shadow: 'bazaart.style.shadow',
  adjust: 'bazaart.adjust',
  presets: 'bazaart.presets',
  position: 'bazaart.position',
  aiTools: 'bazaart.ai_tools',
  badge: 'bazaart.badge',
  refine: 'bazaart.cutout.finger',
  /** Editor parity: the editor's Crop tool reports the same `Tool`. */
  crop: 'bazaart.cutout.crop',
} as const

export type BatchToolKey = keyof typeof BATCH_TOOLS

/**
 * Outcome Sources for `Click`.
 * Commit points only — never fire on drag frames or panel close.
 */
export const BATCH_ACTIONS = {
  backgroundSet: 'batch.background.set',
  resizeSet: 'batch.resize.set',
  shadowSet: 'batch.shadow.set',
  positionSet: 'batch.position.set',
  badgeSet: 'batch.badge.set',
  autoAdjust: 'batch.auto_adjust',
  uploadMore: 'batch.upload_more',
  refine: 'batch.refine',
  refineDone: 'batch.refine.done',
  refineCancel: 'batch.refine.cancel',
  /** Crop saved on a photo (Done after a change). */
  cropDone: 'batch.crop.done',
  /** Crop left without saving (Cancel / X / Escape, after any discard prompt). */
  cropCancel: 'batch.crop.cancel',
  presetAdd: 'batch.preset.add',
  presetSave: 'batch.preset.save',
  presetApply: 'batch.preset.apply',
  presetDelete: 'batch.preset.delete',
  delete: 'batch.delete',
  duplicate: 'batch.duplicate',
  sectionCreate: 'batch.section.create',
  sectionRename: 'batch.section.rename',
  sectionDelete: 'batch.section.delete',
  sectionImageMove: 'batch.section.image_move',
  sectionMove: 'batch.section.move',
  /** Whatnot CSV export finished (fires with success/failure counts). */
  whatnotExport: 'batch.whatnot.export',
  editWithAi: 'batch.edit_with_ai',
  /** Fired once when the batch photo limit modal is shown. */
  limitReached: 'batch.limit_reached',
  fullScreenOpen: 'batch.full_screen.open',
  fullScreenClose: 'batch.full_screen.close',
  fullScreenNavigate: 'batch.full_screen.navigate',
} as const

export type BatchActionKey = keyof typeof BATCH_ACTIONS

/**
 * Premium prompt `Source` for the Whatnot CSV export CTA.
 *
 * Deliberately distinct from `BtSubscriptionBatchMagic` (shared by "Download all"
 * and the selection menu) so `Premium Prompt` → `Premium Selected Plan` →
 * `Premium Subscribed` can be attributed to this button alone.
 *
 * Must also be listed in `METSIKON_SOURCES` (Metsikon.tsx) — sources missing from
 * that set do not auto-open the plan chooser.
 */
export const WHATNOT_EXPORT_PREMIUM_SOURCE = 'BtSubscriptionWhatnotExport'

/**
 * Premium prompt `Source` shown to non-premium users right after their first
 * photos land in an empty batch (file picker or drag & drop).
 *
 * Must also be listed in `METSIKON_SOURCES` (Metsikon.tsx) — sources missing from
 * that set do not auto-open the plan chooser.
 */
export const BATCH_UPLOAD_PREMIUM_SOURCE = 'BtSubscriptionBatchUpload'

/** Open a Batch tool (rail / refine). Never fire on close. */
export function trackBatchToolSelected(
  tool: BatchToolKey,
  extra?: Record<string, unknown>,
): void {
  customAmplitude('Selected tool', {
    Tool: BATCH_TOOLS[tool],
    Placement: BATCH_PLACEMENT,
    ...extra,
  })
}

/** Commit a Batch outcome (set bg / shadow / resize / refine done / …). */
export function trackBatchAction(
  action: BatchActionKey,
  extra?: Record<string, unknown>,
): void {
  customAmplitude('Click', {
    Source: BATCH_ACTIONS[action],
    ...extra,
  })
}
